/* eslint-disable react-hooks/exhaustive-deps */
import { useQuery } from "@apollo/client";
import type { IFilterOptions, IOptionsProps } from "@fluidattacks/design";
import type { IOptions } from "@fluidattacks/design";
import { capitalize, isEmpty } from "lodash";
import { useCallback, useContext, useMemo } from "react";

import {
  updateFindingFiltersStore,
  useGetFindingFiltersStore,
} from "./use-finding-filters-store";

import { GET_FINDING_FILTERS_INFO } from "../../queries";
import { meetingModeContext } from "context/meeting-mode";
import {
  FindingLastReportInterval,
  SeverityRating,
  VulnerabilityOrigin,
  VulnerabilityTreatment,
  VulnerabilityVerification,
} from "gql/graphql";
import type { FindingFiltersInput, Technique } from "gql/graphql";
import { useAuthz } from "hooks/use-authz";
import { useFiltersStorage } from "storage/filters";
import {
  formatLastReportInterval,
  formatReattack,
  formatTreatment,
} from "utils/format-helpers";
import { Logger } from "utils/logger";
import { msgError } from "utils/notifications";
import { translate } from "utils/translations/translate";

interface IFindingsQuery {
  filterOptions: IOptionsProps<FindingFiltersInput>[];
  filtersInfo: {
    findingTitles: string[];
    findingAssignees: string[];
    findingTags: string[];
  };
  getValues: () => string | null;
  setValues: (value: string) => void;
  onFiltersChange: (
    filters: readonly IFilterOptions<FindingFiltersInput>[],
  ) => void;
}

const TECHNIQUES = ["CSPM", "DAST", "PTAAS", "RE", "SAST", "SCA", "SCR"];
const TREATMENTS = [...Object.values(VulnerabilityTreatment)];
const LAST_REPORT_INTERVALS = [...Object.values(FindingLastReportInterval)];
const REATTACK_OPTIONS = [...Object.values(VulnerabilityVerification)].map(
  (value): string =>
    value === VulnerabilityVerification.NotRequested ? "NotRequested" : value,
);
const SEVERITY_RATINGS = [...Object.values(SeverityRating)].filter(
  (value): boolean => value !== SeverityRating.None,
);

const useFindingFilters = ({
  groupName,
  loading,
}: {
  groupName: string;
  loading: boolean;
}): IFindingsQuery => {
  const { can } = useAuthz();
  const { setValues, getValues } = useFiltersStorage();
  const { meetingMode } = useContext(meetingModeContext);

  const {
    title,
    assignees,
    autoFixable,
    where,
    lastReportInterval,
    state,
    severityRating,
    treatment,
    tags,
    origin,
    reattack,
    search,
    technique,
    maxPriority,
    minPriority,
    maxSeverity,
    minSeverity,
    reportedBefore,
    reportedAfter,
    lastReportAfter,
    lastReportBefore,
  } = useGetFindingFiltersStore(groupName);

  const canSeeDraft = can("see_draft_status");

  const { data } = useQuery(GET_FINDING_FILTERS_INFO, {
    fetchPolicy: "no-cache",
    onError: ({ graphQLErrors }): void => {
      graphQLErrors.forEach((error): void => {
        msgError(translate.t("groupAlerts.errorTextsad"));
        Logger.warning("An error occurred loading group data", error);
      });
    },
    variables: { groupName },
  });

  const filtersInfo = useMemo(
    (): {
      findingTitles: string[];
      findingAssignees: string[];
      findingTags: string[];
    } => ({
      findingAssignees: data?.group.findingStats.assignees ?? [],
      findingTags: data?.group.findingStats.tags ?? [],
      findingTitles: Array.from(
        new Set(
          data?.group.findings?.map(
            (finding): string => finding?.title ?? "",
          ) ?? [],
        ),
      ),
    }),
    [data],
  );

  const filterOptions = useMemo(
    (): IOptionsProps<FindingFiltersInput>[] => [
      {
        filterOptions: [
          {
            key: "title",
            label: "Weakness",
            options: [
              ...filtersInfo.findingTitles.map(
                (value): IOptions => ({
                  checked: !isEmpty(title) && title?.includes(value),
                  label: value,
                  value,
                }),
              ),
              ...(isEmpty(search)
                ? []
                : [{ checked: true, label: search, value: search ?? "" }]),
            ],
            type: "checkboxes",
          },
        ],
        group: "Vulnerability information",
        icon: "file-exclamation",
        label: "Weakness",
      },
      {
        filterOptions: [
          { key: "where", label: "Location is", type: "text", value: where },
        ],
        group: "Vulnerability information",
        icon: "file-exclamation",
        label: "Location",
      },
      {
        filterOptions: [
          {
            key: "state",
            label: "Status",
            options: [
              {
                checked: state?.[0] === "VULNERABLE",
                label: "Open",
                value: "VULNERABLE",
              },
              {
                checked: state?.[0] === "SAFE",
                label: "Closed",
                value: "SAFE",
              },
              ...(canSeeDraft && !meetingMode
                ? [
                    {
                      checked: state?.[0] === "DRAFT",
                      label: "Draft",
                      value: "DRAFT",
                    },
                  ]
                : []),
            ],
            type: "radioButton",
          },
        ],
        group: "Vulnerability information",
        icon: "file-exclamation",
        label: "Status",
      },
      {
        filterOptions: [
          {
            key: "origin",
            label: "Origin",
            options: Object.values(VulnerabilityOrigin).map(
              (value): IOptions => ({
                checked: origin === value,
                label: capitalize(value),
                value,
              }),
            ),
            type: "radioButton",
          },
        ],
        group: "Vulnerability information",
        icon: "file-exclamation",
        label: "Origin",
      },
      {
        filterOptions: [
          {
            hideSearch: true,
            key: "severityRating",
            label: "Severity",
            options: SEVERITY_RATINGS.map(
              (value): IOptions => ({
                checked: severityRating?.includes(value),
                label: capitalize(value),
                value,
              }),
            ),
            type: "checkboxes",
          },
          {
            key: "maxSeverity",
            label: "Or define severity range here",
            maxValue: maxSeverity ?? undefined,
            minValue: minSeverity ?? undefined,
            type: "numberRange",
          },
        ],
        group: "Risk information",
        icon: "gauge",
        label: "Severity",
      },
      {
        filterOptions: [
          {
            key: "maxPriority",
            label: "Priority score range",
            maxValue: maxPriority ?? undefined,
            minValue: minPriority ?? undefined,
            placeholder: "%",
            type: "numberRange",
          },
        ],
        group: "Risk information",
        icon: "gauge",
        label: "Priority score",
        placeholder: "%",
      },
      {
        filterOptions: [
          {
            key: "lastReportInterval",
            label: "",
            options: LAST_REPORT_INTERVALS.map(
              (value): IOptions => ({
                checked: lastReportInterval === value,
                label: formatLastReportInterval(value),
                value,
              }),
            ),
            type: "radioButton",
          },
          {
            key: "lastReportAfter",
            label: "Or define last report range here",
            maxValue: lastReportBefore,
            minValue: lastReportAfter,
            type: "dateRange",
          },
        ],
        group: "Report date",
        icon: "clock",
        label: "Last reported (days)",
      },
      {
        filterOptions: [
          {
            key: "reportedAfter",
            label: "Define a report date range here",
            maxValue: reportedBefore,
            minValue: reportedAfter,
            type: "dateRange",
          },
        ],
        group: "Report date",
        icon: "clock",
        label: "Release date",
      },
      {
        filterOptions: [
          {
            hideSearch: true,
            key: "technique",
            label: "Technique",
            options: TECHNIQUES.map(
              (value): IOptions => ({
                checked: technique?.includes(value as Technique),
                label: value,
                value,
              }),
            ),
            type: "checkboxes",
          },
        ],
        group: "Technique",
        icon: "bullseye",
        label: "Technique",
      },
      {
        filterOptions: [
          {
            hideSearch: true,
            key: "treatment",
            label: "Treatment",
            options: TREATMENTS.map(
              (value): IOptions => ({
                checked: treatment?.includes(value),
                label: formatTreatment(value),
                value: value.toUpperCase(),
              }),
            ),
            type: "checkboxes",
          },
        ],
        group: "Vulnerability management",
        icon: "wrench",
        label: "Treatment",
      },
      {
        filterOptions: [
          {
            hideSearch: true,
            key: "reattack",
            label: "Reattack",
            options: REATTACK_OPTIONS.map(
              (value): IOptions => ({
                checked: reattack?.includes(value),
                label: formatReattack(
                  value === "NotRequested" ? value : capitalize(value),
                ),
                value,
              }),
            ),
            type: "checkboxes",
          },
        ],
        group: "Vulnerability management",
        icon: "wrench",
        label: "Reattack",
      },
      {
        filterOptions: [
          {
            key: "assignees",
            label: "Assignees",
            options: filtersInfo.findingAssignees.map(
              (assignee): IOptions => ({
                checked: assignees?.includes(assignee),
                label: assignee,
                value: assignee,
              }),
            ),
            type: "checkboxes",
          },
        ],
        group: "Vulnerability management",
        icon: "wrench",
        label: "Assignees",
      },
      {
        filterOptions: [
          {
            key: "autoFixable",
            label: "Fix with AI",
            options: [
              { checked: autoFixable, label: "Yes", value: "true" },
              { checked: autoFixable === false, label: "No", value: "false" },
            ],
            type: "radioButton",
          },
        ],
        group: "Vulnerability management",
        icon: "wrench",
        label: "Fix with AI",
      },
      {
        filterOptions: [
          {
            key: "tags",
            label: "Tags",
            options: filtersInfo.findingTags.map(
              (tag): IOptions => ({
                checked: tags?.includes(tag),
                label: tag,
                value: tag,
              }),
            ),
            type: "checkboxes",
          },
        ],
        group: "Vulnerability management",
        icon: "tags",
        label: "Tags",
      },
    ],
    [
      filtersInfo.findingTitles,
      canSeeDraft,
      assignees?.[0],
      tags?.[0],
      technique?.[0],
      severityRating?.[0],
      search,
      state?.[0],
      treatment?.[0],
      maxSeverity,
      minSeverity,
      reattack?.[0],
      origin,
      reportedBefore,
      reportedAfter,
      lastReportAfter,
      lastReportBefore,
      autoFixable,
    ],
  );

  const handleGetValue = useCallback((): string | null => {
    return getValues("findings", groupName) as string | null;
  }, [getValues, groupName]);

  const handleSetValue = useCallback(
    (value: string): void => {
      void setValues("findings", value, groupName);
    },
    [setValues, groupName],
  );

  const onFiltersChange = useCallback(
    (filters: readonly IFilterOptions<FindingFiltersInput>[]): void => {
      if (!loading) {
        updateFindingFiltersStore(groupName, filters);
      }
    },
    [loading, groupName],
  );

  return {
    filterOptions,
    filtersInfo,
    getValues: handleGetValue,
    onFiltersChange,
    setValues: handleSetValue,
  };
};

export { useFindingFilters };
export type { IFindingsQuery };
